import { LottieLoading, OspModal, ospThemes } from "@ospweb/sdk-ui";
import { ReactNode, useCallback } from "react";

import { cn } from "utils/src/cn";
import { createCallable } from "react-call";
import { useSnapshot } from "valtio";
import { formatDisplay } from "../table/dashboard/columns/formatDisplay";
import EmptyState from "./EmptyState";
import { Input } from "./Input";
import TokenItem from "./TokenItem";
import Translations from "@translate/components/Watchlist/AddWatchlistModal";
import { I18nUtil } from "@utils";
// import { Spin } from "antd";
import { rootStore } from "@store";
import { useSearchToken } from "@hooks";

const I18n = I18nUtil.createScoped(Translations);

// 导入您现有的LottieLoading组件

interface SpinProps {
  spinning?: boolean;
  children?: ReactNode;
  className?: string;
  fullScreen?: boolean;
}

export const Spin: React.FC<SpinProps> = ({
  spinning = false,
  children,
  fullScreen = false,
  className,
}) => {
  // 如果不需要显示加载状态，直接返回子元素
  if (!spinning && !fullScreen) {
    return <>{children}</>;
  }

  const spinElement = (
    <div
      className={`flex flex-col items-center justify-center ${fullScreen ? "fixed inset-0 z-50 bg-background_solid_secondary/80" : ""}`}
    >
      <div className="flex flex-col items-center">
        {/* 使用现有的LottieLoading组件 */}
        <LottieLoading />
      </div>
    </div>
  );

  // 全屏模式直接返回加载元素
  if (fullScreen) {
    return spinElement;
  }

  // 包含子元素的模式
  return (
    <div className={cn("relative w-full h-full", className)}>
      {children}
      {spinning && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background_solid_secondary/80">
          {spinElement}
        </div>
      )}
    </div>
  );
};

export const AddWatchlistModal = ({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) => {
  const { data, onSearch, isLoading, isSearch, setIsSearch } = useSearchToken();
  const { getTokenByPool, getCoinmarketcapTokenByPool } = useSnapshot(
    rootStore.token,
  );

  // v1.17目前暂时没有收藏列表， 通过tags区分数据源的逻辑后续有了相应业务再进行调整
  // Optimized getVol24 function
  const getVol24 = useCallback(() => {
    return (item: any) => {
      const vol24 = item.tags?.includes("blue_chip")
        ? getCoinmarketcapTokenByPool(item.poolAddress)?.vol_24h_usdt
        : getTokenByPool(item.poolAddress)?.vol_24h_usdt;

      return formatDisplay([vol24, item?.trade?.vol_24h_usdt], "$");
    };
  }, [getTokenByPool, getCoinmarketcapTokenByPool]);

  const handleClose = () => {
    onClose();
    onSearch("");
    setIsSearch(false);
  };

  return (
    <OspModal
      open={visible}
      onClose={handleClose}
      closable={false}
      containerClassName="max-w-full !md:w-[911px] min-h-[480px]"
      contentStyle={{
        background: ospThemes.colors.background_solid_secondary,
        width: "100%",
      }}
      showClose={false}
      centered
      destroyOnClose
    >
      <div className="flex-center gap-3 mb-[16px]">
        <Input
          onChange={(e) => {
            onSearch(e);
          }}
          onClear={() => {
            onSearch("");
            setIsSearch(false);
          }}
        />
        <button
          aria-label="Add Watchlist Modal"
          data-openreplay-label={`Add Watchlist Modal Button | ${I18n.t("Cancel")}`}
          id="btn-add-watchlist-modal"
          className="body_s text-content_tertiary"
          type="button"
          onClick={handleClose}
        >
          {I18n.t("Cancel")}
        </button>
      </div>
      <Spin
        className="w-full min-h-[196px]"
        spinning={isLoading}
        // wrapperClassName="!bg-transparent"
      >
        <div className="flex flex-col gap-2 max-h-[448px] overflow-y-auto no-scrollbar">
          {data?.map((item, index) => (
            <TokenItem
              key={`${item.address}-${index}`}
              item={item}
              index={index}
              getVol24={getVol24}
              callback={handleClose}
            />
          ))}
        </div>
        {!data?.length && !isLoading && <EmptyState isSearch={isSearch} />}
      </Spin>
    </OspModal>
  );
};

// Instead of creating our own types, let's use type assertions to work with the existing types
// and avoid compatibility issues

export const AddWatchlistCreateCallable = createCallable<void, void>(
  ({ call }) => {
    return <AddWatchlistModal visible={true} onClose={() => call.end()} />;
  },
);
